import { I18n } from '../../i18n/mx-internationalization';
import { SystemStorageType } from '../model/system-storage-types';

const expirationKey = '__expiration__';

type StorageOnChangedCallback = () => void;
type DataInstancesStorage = {
	[key in SystemStorageType]?: BaseDataProvider;
};

/**
 * @ngdoc service
 * @name DataStorage
 * @module mx.app
 *
 * @requires $document
 *
 * @description
 * Provides a functionality for permanent storage of data between sessions.<br>
 * Data can be saved either on Cookies or in Local Storage
 */
export class DataStorage {
	static readonly $inject = ['mx.internationalization'];
	storageInstances: DataInstancesStorage = {};
	constructor(private readonly i18n: I18n) {
		this.registerStorageInstance(SystemStorageType.LocalStorage, LocalStorageDataProvider);
		this.registerStorageInstance(SystemStorageType.Cookies, CookiesDataProvider);
	}

	/**
	 * @ngdoc method
	 * @name DataStorage#getStorage
	 *
	 * @description
	 * Gets the reference to the storage
	 *
	 * @param {string} key - Key of the storage
	 *
	 * @returns {BaseDataProvider} Data storage
	 */
	getStorage(key: SystemStorageType): IDataProvider {
		const instance = this.storageInstances[key];

		if (!instance) {
			throw new Error('Storage ' + key + ' not found');
		}
		return instance;
	}
	//new(userIdentity: string, i18n: I18n) => BaseDataProvider
	registerStorageInstance(key: SystemStorageType, StorageClass: new (i18n: I18n) => BaseDataProvider) {
		this.storageInstances[key] = new StorageClass(this.i18n);
	}
}

export interface IDataProvider {
	get(key: string): null | string;
	getAsObject(key: string): null | unknown;
	set(key: string, value: unknown, expiration?: number): boolean;
	setAsObject(key: string, value: unknown, expiration?: number): boolean;
	remove(key: string): boolean;
	onChanged(key: string, func: StorageOnChangedCallback): boolean;
}

abstract class BaseDataProvider implements IDataProvider {
	constructor(private readonly i18n: I18n) {}

	abstract _set(key: string, value: unknown): boolean;
	abstract _get(key: string): string | null;
	abstract _remove(key: string): boolean;
	abstract _onChanged(key: string, func: () => void): void;

	_getKey(key: string): string {
		return key;
	}

	get(key: string): null | string {
		return !key || this._checkIfExpired(key) ? null : this._get(this._getKey(key));
	}

	getAsObject(key: string): null | unknown {
		const value = this.get(key);

		return value ? JSON.parse(value) : null;
	}

	// "expiration" - not mandatory (in minutes)
	set(key: string, value: unknown, expiration?: number): boolean {
		if (!key) {
			return false;
		}
		this._saveExpiration(key, expiration);
		return this._set(this._getKey(key), value);
	}

	// "expiration" - not mandatory (in minutes)
	setAsObject(key: string, value: unknown, expiration?: number): boolean {
		this.set(key, value ? JSON.stringify(value) : null, expiration);
		return true;
	}

	remove(key: string): boolean {
		if (!key) {
			return false;
		}
		return this._remove(this._getKey(key));
	}

	onChanged(key: string, func: StorageOnChangedCallback): boolean {
		if (!key) {
			return false;
		}
		if (!func) {
			return false;
		}
		this._onChanged(key, func);
		return true;
	}

	_saveExpiration(key: string, expireTime?: number): void {
		let expirations: Record<string, number>;
		const eTime = Number(expireTime);

		if (expireTime && expireTime > 0) {
			expirations = (this.getAsObject(expirationKey) as Record<string, number>) || {};
			const hour = 60 * 60 * 1000;
			const expiredHours = eTime * hour;

			expirations[key] = new Date().getTime() + expiredHours;
			this.setAsObject(expirationKey, expirations);
		}
	}

	_checkIfExpired(key: string): boolean {
		let expirations: Record<string, number>;
		let isExpired = false;

		if (key !== expirationKey) {
			expirations = (this.getAsObject(expirationKey) as Record<string, number>) || {};
			if (expirations[key]) {
				isExpired = new Date().getTime() > Number(expirations[key]);
				// Clean if expired
				if (isExpired) {
					this.remove(key);
					delete expirations[key];
					this.setAsObject(expirationKey, expirations);
				}
			}
		}
		return isExpired;
	}

	cleanAllWithExpirationDate() {
		const expirations = (this.getAsObject(expirationKey) as Record<string, number>) || {};

		for (const key in expirations) {
			if (Object.prototype.hasOwnProperty.call(expirations, key)) {
				this.remove(key);
			}
		}
		this.remove(expirationKey);
	}
}

export class LocalStorageDataProvider extends BaseDataProvider {
	constructor(i18n: I18n) {
		super(i18n);
		if (typeof Storage === 'undefined') {
			throw new Error(i18n.get('shell.errors.browser_not_support_storage'));
		}
	}
	_get(key: string) {
		return localStorage.getItem(key);
	}
	_set(key: string, value: string) {
		localStorage.setItem(key, value);
		return true;
	}
	_remove(key: string) {
		localStorage.removeItem(key);
		return true;
	}
	_onChanged(key: string, func: StorageOnChangedCallback) {
		window.addEventListener(
			'storage',
			function (event) {
				if (event.key === key) {
					func();
				}
			},
			false
		);
	}
}

export class CookiesDataProvider extends BaseDataProvider {
	_get(key: string) {
		return this.getCookie(key);
	}
	_set(key: string, value: string) {
		return this.setCookie(key, value);
	}
	_remove(key: string) {
		return this.removeItem(key);
	}
	_onChanged(/*key: string, func: StorageOnChangedCallback*/) {
		throw new Error('Cookies DataProvider has no implementation of onChanged');
	}

	private getCookie(name: string): string | null {
		if (!name || !document.cookie) {
			return null;
		}
		return (
			decodeURIComponent(
				document.cookie.replace(new RegExp('(?:(?:^|.*;)\\s*' + encodeURIComponent(name).replace(/[-.+*]/g, '\\$&') + '\\s*\\=\\s*([^;]*).*$)|^.*$'), '$1')
			) || null
		);
	}

	private setCookie(name: string, value: string, end?: number | string | Date, path = '', domain = '', secure = false): boolean {
		if (!name || /^(?:expires|max-age|path|domain|secure)$/i.test(name)) {
			return false;
		}
		let expires = '';

		if (end) {
			if (end instanceof Number) {
				expires = end === Infinity ? '; expires=Fri, 31 Dec 9999 23:59:59 GMT' : '; max-age=' + end;
			} else if (end instanceof String) {
				expires = '; expires=' + end;
			} else if (end instanceof Date) {
				expires = '; expires=' + end.toUTCString();
			}
		}
		document.cookie =
			encodeURIComponent(name) +
			'=' +
			encodeURIComponent(value) +
			expires +
			(domain ? '; domain=' + domain : '') +
			(path ? '; path=' + path : '') +
			(secure ? '; secure' : '');
		return true;
	}

	private hasItem(name: string): boolean {
		if (!name) {
			return false;
		}
		return new RegExp('(?:^|;\\s*)' + encodeURIComponent(name).replace(/[-.+*]/g, '\\$&') + '\\s*\\=').test(document.cookie);
	}

	private removeItem(name: string, path = '', domain = ''): boolean {
		if (!this.hasItem(name)) {
			return false;
		}
		document.cookie = encodeURIComponent(name) + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT' + (domain ? '; domain=' + domain : '') + (path ? '; path=' + path : '');
		return true;
	}
}
